import {
  Confirmation,
  ConfirmationAccepted,
  ConfirmationAction,
  ConfirmationActions,
  ConfirmationDescription,
  ConfirmationTitle,
} from "@/components/ai-elements/confirmation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi, useApiRequest, useApiStream } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import {
  CHAT_STACK_GAP_CLASS,
  type ChatAssistantMetaInfo,
} from "@/components/chat/chat-dialog-primitives";
import { cn } from "@/lib/utils";
import { useChat } from "@ai-sdk/react";
import { TeamInstructionConversationDTO } from "@shaarp/common";
import { DefaultChatTransport, type UIMessage } from "ai";
import { CheckIcon, SparklesIcon, XIcon } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import PageSkeleton from "../page-skeleton";
import { ConversationCloseDialog } from "./instruction-conversation/conversation-close-dialog";
import { ConversationFooter } from "./instruction-conversation/conversation-footer";
import { ConversationGenerationPlan } from "./instruction-conversation/conversation-generation-plan";
import { ConversationMessage } from "./instruction-conversation/conversation-message";
import {
  isResetSuggestion,
  isSystemTrigger,
  parseSuggestions,
} from "./instruction-conversation/conversation-utils";

const submittedIndicator = (
  <div className="flex gap-2.5 items-center h-7">
    <span className="flex gap-1">
      <span className="rounded-full bg-majorelle size-1.5 thinking-dot" />
      <span className="rounded-full bg-majorelle size-1.5 thinking-dot [animation-delay:0.2s]" />
      <span className="rounded-full bg-majorelle size-1.5 thinking-dot [animation-delay:0.4s]" />
    </span>
  </div>
);

export type TeamInstructionConversationHandle = {
  requestClose: () => void;
};

export const TeamInstructionConversation = forwardRef<
  TeamInstructionConversationHandle,
  {
    onClose: () => void;
    onSaved: () => void;
    hasExistingInstructions: boolean;
  }
>(function TeamInstructionConversation(
  { onClose, onSaved, hasExistingInstructions },
  ref,
) {
  const api = useApi();
  const { url, headers } = useApiRequest();
  const apiStream = useApiStream();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t, currentLanguage } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [editableGenerated, setEditableGenerated] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [shouldTriggerIntro, setShouldTriggerIntro] = useState(false);
  const [shouldTriggerResume, setShouldTriggerResume] = useState(false);
  const [isReadyToGenerate, setIsReadyToGenerate] = useState(false);
  const [isPlanOpen, setIsPlanOpen] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useImperativeHandle(ref, () => ({
    requestClose: () => setShowCloseConfirm(true),
  }));

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { messages, sendMessage, status, stop, setMessages } = useChat<
    UIMessage<ChatAssistantMetaInfo>
  >({
    transport: new DefaultChatTransport({
      api: url("/my/team/instruction/conversation/message"),
      headers: () => headers(),
      body: { language: currentLanguage },
    }),
    onFinish: () => {
      refreshReadiness();
    },
    onError: () => {
      toastError();
    },
  });

  const isStreaming = status === "streaming" || status === "submitted";

  const suggestions = useMemo(() => {
    if (isStreaming) return [];
    const lastAssistant = [...messages]
      .reverse()
      .find((m) => m.role === "assistant");
    if (!lastAssistant) return [];
    const text = lastAssistant.parts
      .filter((p) => p.type === "text")
      .map((p) => p.text)
      .join("");
    return parseSuggestions(text);
  }, [messages, isStreaming]);

  const handleSuggestionClick = (suggestion: string): void => {
    if (isStreaming) return;
    if (isResetSuggestion(suggestion)) {
      setShowResetConfirm(true);
      return;
    }
    setInputValue("");
    setEditableGenerated("");
    sendMessage({
      role: "user",
      parts: [{ type: "text", text: suggestion }],
    });
  };

  useEffect(() => {
    fetchOrCreateConversation();
  }, []);

  useEffect(() => {
    if (shouldTriggerIntro && !isLoading) {
      setShouldTriggerIntro(false);
      sendMessage({
        role: "user",
        parts: [{ type: "text", text: "Start" }],
      });
    }
  }, [shouldTriggerIntro, isLoading]);

  useEffect(() => {
    if (shouldTriggerResume && !isLoading) {
      setShouldTriggerResume(false);
      sendMessage({
        role: "user",
        parts: [{ type: "text", text: "Resume" }],
      });
    }
  }, [shouldTriggerResume, isLoading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isGenerating) {
      scrollToBottom();
    }
  }, [isGenerating]);

  useEffect(() => {
    if (!isStreaming) {
      inputRef.current?.focus();
    }
  }, [isStreaming]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }, []);

  const refreshReadiness = async (): Promise<void> => {
    try {
      const { data } = await api.get<TeamInstructionConversationDTO | null>(
        "/my/team/instruction/conversation"
      );
      if (data) {
        setIsReadyToGenerate(data.isReadyToGenerate);
      }
    } catch {
      /* silent — non-critical */
    }
  };

  const fetchOrCreateConversation = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const { data: existing } =
        await api.get<TeamInstructionConversationDTO | null>(
          "/my/team/instruction/conversation"
        );

      if (existing) {
        setIsReadyToGenerate(existing.isReadyToGenerate);
        setMessages([]);
        setShouldTriggerResume(true);
      } else {
        await api.post<TeamInstructionConversationDTO>(
          "/my/team/instruction/conversation"
        );
        setMessages([]);
        if (hasExistingInstructions) {
          setShouldTriggerResume(true);
        } else {
          setShouldTriggerIntro(true);
        }
      }
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (): Promise<void> => {
    const text = inputValue.trim();
    if (!text || isStreaming) return;

    setInputValue("");
    setEditableGenerated("");

    sendMessage({
      role: "user",
      parts: [{ type: "text", text }],
    });

    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleGenerate = async (): Promise<void> => {
    setIsGenerating(true);
    setIsPlanOpen(false);
    let fullText = "";

    try {
      await apiStream.post(
        "/my/team/instruction/conversation/generate",
        { language: currentLanguage },
        {
          onOpen() {
            setEditableGenerated("");
          },
          onMessage(eventSourceMessage) {
            if (eventSourceMessage?.data) {
              try {
                const event = JSON.parse(eventSourceMessage.data);
                if (event.type === "text-delta" && event.delta) {
                  fullText += event.delta;
                  setEditableGenerated(fullText);
                }
              } catch {
                /* skip unparseable lines */
              }
            }
          },
          onClose() {},
          onError(error: Error) {
            throw error;
          },
        }
      );
    } catch {
      toastError();
      setEditableGenerated("");
    } finally {
      setIsGenerating(false);
      if (fullText) {
        setIsPlanOpen(true);
      }
    }
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      await api.post("/my/team/instruction/conversation/save", {
        content: editableGenerated,
      });
      toast({
        variant: "default",
        title: t("team.components.instruction.conversation.toast.saved.title"),
        description: t(
          "team.components.instruction.conversation.toast.saved.description"
        ),
      });
      onSaved();
    } catch {
      toastError();
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async (): Promise<void> => {
    setIsResetting(true);
    try {
      await api.delete("/my/team/instruction/conversation");
      await api.post<TeamInstructionConversationDTO>(
        "/my/team/instruction/conversation"
      );
      setMessages([]);
      setEditableGenerated("");
      setIsReadyToGenerate(false);
      setShouldTriggerIntro(true);
      toast({
        variant: "default",
        title: t("team.components.instruction.conversation.toast.reset.title"),
        description: t(
          "team.components.instruction.conversation.toast.reset.description"
        ),
      });
    } catch {
      toastError();
    } finally {
      setIsResetting(false);
    }
  };

  const handleCloseAndForget = async (): Promise<void> => {
    try {
      await api.delete("/my/team/instruction/conversation");
    } catch {
      /* silent — closing anyway */
    }
    onClose();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {t("team.components.instruction.conversation.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PageSkeleton />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>
              {t("team.components.instruction.conversation.title")}
            </CardTitle>
            <CardDescription>
              {t("team.components.instruction.conversation.description")}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowCloseConfirm(true)}
          >
            <XIcon className="w-4 h-4" />
          </Button>
          <ConversationCloseDialog
            open={showCloseConfirm}
            onOpenChange={setShowCloseConfirm}
            onCloseAndForget={handleCloseAndForget}
            onCloseAndRemember={onClose}
          />
          <AlertDialog
            open={showResetConfirm}
            onOpenChange={setShowResetConfirm}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t(
                    "team.components.instruction.conversation.resetConfirm.title"
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    "team.components.instruction.conversation.resetConfirm.description"
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  {t(
                    "team.components.instruction.conversation.resetConfirm.cancel"
                  )}
                </AlertDialogCancel>
                <AlertDialogAction onClick={handleReset}>
                  {t(
                    "team.components.instruction.conversation.resetConfirm.confirm"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        {/* The one gap a conversation stacks with: `ChatAssistantMeta` is
            overlaid below its message and is taller than 24px, so a container
            of its own spilled it onto the message underneath. */}
        <div className={cn("flex flex-col max-w-none", CHAT_STACK_GAP_CLASS)}>
          {messages
            .filter((m) => !(m.role === "user" && isSystemTrigger(m)))
            .map((message, _idx, filtered) => (
              <ConversationMessage
                key={message.id}
                message={message}
                isStreaming={
                  isStreaming &&
                  message.id === filtered[filtered.length - 1]?.id
                }
              />
            ))}
          {status === "submitted" && submittedIndicator}
          {isReadyToGenerate &&
            !isStreaming &&
            !isGenerating &&
            !editableGenerated &&
            messages.length >= 2 && (
              <Confirmation>
                <ConfirmationTitle>
                  {t(
                    "team.components.instruction.conversation.confirmation.title"
                  )}
                </ConfirmationTitle>
                <ConfirmationDescription>
                  {t(
                    "team.components.instruction.conversation.confirmation.description"
                  )}
                </ConfirmationDescription>
                <ConfirmationActions className="justify-end">
                  <ConfirmationAction onClick={handleGenerate}>
                    <SparklesIcon className="mr-2 size-4" />
                    {t(
                      "team.components.instruction.conversation.generateButton"
                    )}
                  </ConfirmationAction>
                </ConfirmationActions>
              </Confirmation>
            )}
          {(isGenerating || !!editableGenerated) && (
            <ConfirmationAccepted>
              <CheckIcon className="size-4 text-green-500" />
              {t(
                "team.components.instruction.conversation.confirmation.accepted"
              )}
            </ConfirmationAccepted>
          )}
          {(isGenerating || !!editableGenerated) && (
            <ConversationGenerationPlan
              isGenerating={isGenerating}
              isPlanOpen={isPlanOpen}
              onPlanOpenChange={setIsPlanOpen}
              editableGenerated={editableGenerated}
              isSaving={isSaving}
              onSave={handleSave}
            />
          )}
          <div ref={messagesEndRef} />
        </div>
      </CardContent>
      <ConversationFooter
        suggestions={suggestions}
        onSuggestionClick={handleSuggestionClick}
        inputValue={inputValue}
        onInputChange={setInputValue}
        inputRef={inputRef}
        onSubmit={handleSendMessage}
        isStreaming={isStreaming}
        onStop={stop}
        isReadyToGenerate={isReadyToGenerate}
        onGenerate={handleGenerate}
        isResetting={isResetting}
        onReset={handleReset}
        messageCount={messages.length}
      />
    </Card>
  );
});
